import { Component } from '@angular/core';
import AccountClosureModel from 'src/app/services/accountClosure.model';
import { AccountClosureService } from 'src/app/services/accountClosure.service';
import BusinessEntity from 'src/app/services/businessEntity.model';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { ActivatedRoute, Router } from '@angular/router';
import { BusinessEntityService } from 'src/app/services/businessEntity.service';
import { FirebaseNotificationsMessagingService } from 'src/app/services/FirebaseMessaging.service';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

declare var $: any;

/**
 * AccountClosureDialogPopupsComponent
 *
 * This component manages the dialog popups for account closure and reactivation requests.
 * It handles user interactions for confirming or cancelling account status changes.
 *
 * Key Responsibilities:
 * - Display confirmation dialogs for account closure or reactivation.
 * - Handle user confirmation to proceed with the request.
 * - Handle user cancellation of the request.
 * - Update the account status via `AccountClosureService`.
 * - Display success notifications upon successful status update.
 * - Navigate the user to the appropriate route after action completion.
 *
 * User Roles:
 * - Business Admin: Can request account closure or reactivate a closed account.
 * - Farm Manager/Farmer: May view status but typically managed by Business Admin.
 *
 * Features:
 * - **Dynamic Dialog Content**: Messages and buttons change based on current account status.
 * - **Status Management**: Handles transitions between 'active', 'markedForInactivation', and 'inactivated'.
 * - **Service Integration**: Communicates with backend services for status updates.
 * - **Navigation Handling**: Redirects users based on their role and previous route.
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Retrieves previous route and business details.
 * 3. Determines dialog content based on `business.accountStatus`.
 * 4. Displays the notification modal.
 * 5. User action (Submit/Cancel) triggers `onSubmittingTheRequest` or `onCancelingTheRequest`.
 * 6. `onSubmittingTheRequest` updates status via service and shows success modal.
 * 7. `onCloseSucessfullPopup` navigates the user away.
 *
 * @component
 * @selector app-account-closure-dialog-popups
 * @example
 * <app-account-closure-dialog-popups></app-account-closure-dialog-popups>
 *
 * @implements {OnInit}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-account-closure-dialog-popups',
  templateUrl: './account-closure-dialog-popups.component.html',
  styleUrls: ['./account-closure-dialog-popups.component.scss']
})
export class AccountClosureDialogPopupsComponent {
  /**
   * Main message displayed in the dialog.
   * @type {string}
   */
  public message: string;

  /**
   * Sub-message or description displayed in the dialog.
   * @type {string}
   */
  public subMessage: string;

  /**
   * Message displayed in the success popup.
   * @type {string}
   */
  public sucessMessage: string;

  /**
   * Sub-message displayed in the success popup.
   * @type {string}
   */
  public sucessSubMessage: string;

  /**
   * The business entity associated with the account closure request.
   * @type {BusinessEntity}
   */
  public business: BusinessEntity

  /**
   * Label for the submit/confirm button.
   * @type {string}
   */
  public submitBtnName: string;

  /**
   * Label for the cancel/dismiss button.
   * @type {string}
   */
  public cancelBthName: string;

  /**
   * Flag to show confirmation alert (not actively used in current logic).
   * @type {boolean}
   */
  public confirmationAlert: boolean;

  /**
   * Flag to show success alert (not actively used in current logic).
   * @type {boolean}
   */
  public sucessAlert: boolean;

  /**
   * Business category of the logged-in user (e.g., 'Farm', 'Provider').
   * @type {string}
   */
  public loginBusinessCategory: string;

  /**
   * Business ID from route parameters (not actively used in current logic).
   * @type {string}
   */
  public paramsbusinessId: string;

  /**
   * Model for account closure request payload.
   * @type {AccountClosureModel}
   */
  public accountClosureModel: AccountClosureModel;

  /**
   * Flag to enable/disable the request button to prevent multiple clicks.
   * @type {boolean}
   * @default false
   */
  public requestBtnEnableDisable: boolean = false;

  /**
   * Flag to enable/disable the cancel button.
   * @type {boolean}
   * @default false
   */
  public cancelBtnEnableDisable: boolean = false;

  /**
   * The previous route URL for navigation.
   * @type {string}
   */
  public previousRoute: string;

  /**
   * Constructor for AccountClosureDialogPopupsComponent
   *
   * Initializes the component and injects required services.
   *
   * @constructor
   * @param {AccountClosureService} accountClosureService - Service for account closure API operations.
   * @service AccountClosureService
   * @param {ToastrService} toastr - Service for displaying toast notifications.
   * @service ToastrService
   * @param {CommonMethodsService} commonMethodService - Service for common utility methods.
   * @service CommonMethodsService
   * @param {Router} router - Service for navigation.
   * @service Router
   * @param {BusinessEntityService} businessEntityService - Service for Business Entity API operations.
   * @service BusinessEntityService
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @service ActivatedRoute
   * @param {FirebaseNotificationsMessagingService} firebaseMessagingService - Service for Firebase messaging.
   * @service FirebaseNotificationsMessagingService
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(public accountClosureService: AccountClosureService, public toastr: ToastrService, public commonMethodService: CommonMethodsService,
    public router: Router, public businessEntityService: BusinessEntityService, public route: ActivatedRoute,
    private firebaseMessagingService: FirebaseNotificationsMessagingService, public previousRouteService: PreviousRouteService) {
  }

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Retrieves the previous route.
   * 2. Gets the login business category from local storage.
   * 3. Fetches the business entity details using the ID from local storage.
   * 4. Triggers the dialog popup based on the business account status.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    this.previousRoute = await this.previousRouteService.getPreviousRoute();
    this.loginBusinessCategory = localStorage.getItem('loginBusinessCategory');
    this.business = await this.businessEntityService.getOneByID(JSON.parse(localStorage.getItem('businessEntityById')).id);
    if (this.business) {
      this.accountClosureOrActivationRequest(this.business);
    }
  }

  /**
   * Configures and displays the notification popup based on account status.
   *
   * - 'markedForInactivation': Prompt to cancel closure request.
   * - 'inactivated': Prompt to reactivate account.
   * - Default: Prompt to request account closure.
   *
   * @method accountClosureOrActivationRequest
   * @param {BusinessEntity} businessObject - The business entity object.
   * @returns {void}
   */
  public accountClosureOrActivationRequest(businessObject: BusinessEntity) {
    switch (businessObject.accountStatus) {
      case 'markedForInactivation':
        this.message = "You are cancelling the account closure request.";
        this.subMessage = "Click 'Yes' to cancel the account closure request or 'No' to dismiss";
        this.submitBtnName = 'No';
        this.cancelBthName = 'Yes';
        break;

      case 'inactivated':
        this.message = "As per your request, your account has been deactivated";
        this.subMessage = "Click 'Activate' to Re-activate your account or 'No' to cancel";
        this.submitBtnName = 'No';
        this.cancelBthName = 'Activate';
        break;

      default:
        this.message = "You are requesting for account closure.";
        this.subMessage = "Once your account is closed, all your business, location, infra, and transactional data will be deleted permanently. Click 'Close' to proceed with account closure.";
        this.submitBtnName = 'Close';
        this.cancelBthName = 'Cancel';
        break;
    }

    $("#notification").modal({
      backdrop: 'static',
      keyboard: false,
      show: true
    });
  }

  /**
   * Handles the submission of the account closure or activation request.
   *
   * - Determines the new status based on current `accountStatus`.
   * - Prepares the `AccountClosureModel` payload.
   * - Calls `accountClosureService.updateOneByID` to update the status.
   * - Updates local storage with the new status.
   * - Displays a success modal upon successful update.
   * - Handles errors using `commonMethodService`.
   *
   * @async
   * @method onSubmittingTheRequest
   * @returns {Promise<void>}
   * @throws {Error} If the service call fails.
   */
  public async onSubmittingTheRequest() {
    try {
      const accountStatus = this.business.accountStatus;
      const now = new Date();
      const formattedDateTime = moment(now).format('YYYY-MM-DD HH:mm');
      const updatedBy = localStorage.getItem('personID');

      let status: number, stateTrackKey: string, stateTrackBy: string;

      switch (accountStatus) {
        case 'markedForInactivation':
          status = 1; // active
          stateTrackKey = 'activatedOn';
          stateTrackBy = 'activatedBy';
          break;

        case 'inactivated':
          status = 1; // active
          stateTrackKey = 'activatedOn';
          stateTrackBy = 'activatedBy';
          break;

        default:
          status = 2; // markedForInactivation
          stateTrackKey = 'closureRequestedOn';
          stateTrackBy = 'closureRequestedBy';
          break;
      }

      this.accountClosureModel = {
        businessID: this.business.id,
        status,
        updatedBy,
        stateTrack: {
          [stateTrackKey]: formattedDateTime,
          [stateTrackBy]: updatedBy,
        },
        appName: 'blubay'
      };

      delete this.accountClosureModel.businessID;
      delete this.accountClosureModel.updatedBy;
      delete this.accountClosureModel.stateTrack.activatedBy;
      delete this.accountClosureModel.stateTrack.closureRequestedBy;

      const businessAccountStatusUpdated = await this.accountClosureService.updateOneByID(
        this.business.accountID,
        this.accountClosureModel
      );

      if (businessAccountStatusUpdated && businessAccountStatusUpdated.updated) {
        this.requestBtnEnableDisable = false;
        this.cancelBtnEnableDisable = false;

        const updatedBusiness = JSON.parse(localStorage.getItem('businessEntityById'));
        updatedBusiness.accountStatus = status === 2 ? 'markedForInactivation' : (status === 1 ? 'active' : 'cancelRequested');
        localStorage.setItem('businessEntityById', JSON.stringify(updatedBusiness));

        $('#notification').modal('hide');

        if (status === 1) {
          this.sucessMessage = "Account Re-activated sucessfully";
        } else {
          this.sucessMessage = "Your request for account closure registered successfully";
          this.sucessSubMessage = "Our team will initiate the account closure process. We will inform you accordingly";
        }

        $("#sucessNotification").modal({
          backdrop: 'static',
          keyboard: false,
          show: true
        });
      }
    } catch (error) {
      this.requestBtnEnableDisable = false;
      this.cancelBtnEnableDisable = false;
      this.commonMethodService.userFriendlyAlertMessages(error);
      this.onCancelingTheRequest();
    }
  }

  /**
   * Handles the cancellation of the dialog action.
   * Hides the modal and navigates the user back to the previous route or login page.
   *
   * @method onCancelingTheRequest
   * @returns {void}
   */
  public onCancelingTheRequest() {
    $('#notification').modal('hide'); // To hide confirmation notification popup
    if (this.loginBusinessCategory == 'Farm') {
      if (this.business.accountStatus != 'inactivated') {
        this.router.navigate([this.previousRoute]);
      } else {
        this.router.navigate(['/login']);
      }
    } else {
      if (this.business.accountStatus != 'inactivated') {
        this.router.navigate([this.previousRoute]);
      } else {
        this.router.navigate(['/login']);
      }
    }
  }

  /**
   * Handles the closing of the success popup.
   * Hides the modal, triggers a Firebase notification update, and navigates back.
   *
   * @method onCloseSucessfullPopup
   * @returns {void}
   * @side-effect Updates Firebase notifications and navigates.
   */
  public onCloseSucessfullPopup() {
    $('#sucessNotification').modal('hide'); // To hide sucess notification popup
    this.firebaseMessagingService.notifyUpdate(); // To update top nav component
    if (this.loginBusinessCategory == 'Farm') {
      this.router.navigate([this.previousRoute]);
    } else {
      this.router.navigate([this.previousRoute]);
    }
  }
}
